import React, { useEffect, useRef } from 'react';

import { Body, ConsentText, Container, Content, Divider } from './YourConsent.styled';

import { PdfConsentViewer } from './PdfConsentViewer';
import { YourConsentProps } from './YourConsent.types';
import { ConsentAcceptAction } from '../../Components/ConsentAcceptAction/ConsentAcceptAction';
import { ConsentHTMLError } from '../../Components/ConsentHTML/ConsentHTMLError';

export function YourConsent({
  consentRawHtml,
  consentId,
  patientConsentId,
  versionUuid,
  hasMultipleSteps,
  onAgree,
  pdf
}: YourConsentProps) {
  const handlersRef = useRef<Map<string, { element: HTMLInputElement; handler: EventListener }>>(
    new Map()
  );

  useEffect(() => {
    if (!consentRawHtml) return;

    const handlers = handlersRef.current;

    const allFields = document.querySelectorAll(
      '#consentContainer input, #consentContainer textarea'
    );

    const requiredFields = Array.from(allFields).filter(field =>
      field.hasAttribute('data-required-if')
    );

    requiredFields.forEach(field => {
      const inputField = field as HTMLInputElement | HTMLTextAreaElement;
      const requiredIfValue = inputField.getAttribute('data-required-if');

      if (!requiredIfValue) return;

      if (requiredIfValue.split('.').length < 2) return;

      const [fieldGroup, linkedCheckboxId] = requiredIfValue.split('.');

      if (fieldGroup !== 'patient_filled_fields') return;

      if (linkedCheckboxId) {
        const linkedCheckbox = document.getElementById(linkedCheckboxId) as HTMLInputElement;

        if (linkedCheckbox) {
          inputField.disabled = !linkedCheckbox.checked;
          if (inputField.disabled) inputField.value = '';

          const checkboxHandler = () => {
            inputField.disabled = !linkedCheckbox.checked;
            if (inputField.disabled) inputField.value = '';
          };
          linkedCheckbox.addEventListener('change', checkboxHandler);
          handlers.set(linkedCheckboxId, { element: linkedCheckbox, handler: checkboxHandler });
        }
      }
    });

    return () => {
      handlers.forEach(({ element, handler }) => {
        element.removeEventListener('change', handler);
      });
      handlers.clear();
    };
  }, [consentRawHtml]);

  if (!consentRawHtml && !pdf) return <ConsentHTMLError />;

  if (!consentId && !patientConsentId) return <ConsentHTMLError />;

  return (
    <Container>
      <Content>
        {pdf && !consentRawHtml ? (
          <PdfConsentViewer pdf={pdf} />
        ) : (
          <Body id="consentContainer" dangerouslySetInnerHTML={{ __html: consentRawHtml }} />
        )}
        {onAgree && (
          <>
            <Divider />
            <ConsentText>
              {hasMultipleSteps
                ? 'By clicking the button to move to the next section, I acknowledge that I have read and understand the information above.'
                : 'By clicking “I consent”, I agree and acknowledge that I have read and understand the information provided above.'}
            </ConsentText>
            <ConsentAcceptAction
              consentId={consentId}
              patientConsentId={patientConsentId}
              versionUuid={versionUuid}
              onAgree={onAgree}
              buttonText={hasMultipleSteps ? 'Acknowledge and Continue' : 'I consent'}
            />
          </>
        )}
      </Content>
    </Container>
  );
}
